/**
 * liveCandles — fold live ticker prices into loaded candle history.
 * No React / Zustand dependencies; pass raw data in, get candles out.
 */

import type { Candle, TimeframeKey, TokenPrice } from '@arc/shared';
import { CHART_CANDLE_LIMIT } from '@arc/shared';

export const TF_DURATION_S: Record<TimeframeKey, number> = {
  '1m': 60, '5m': 300, '15m': 900, '30m': 1800,
  '1h': 3600, '4h': 14400, '1D': 86400, '1W': 604800,
};

// Unix epoch is a Thursday; exchanges open weekly candles on Monday 00:00 UTC.
const WEEK_OFFSET_S = 4 * 86400;

/** Start (Unix seconds) of the candle slot containing `tsS`. */
export function slotStart(tsS: number, tf: TimeframeKey): number {
  const dur    = TF_DURATION_S[tf];
  const offset = tf === '1W' ? WEEK_OFFSET_S : 0;
  return Math.floor((tsS - offset) / dur) * dur + offset;
}

function isTimeframe(tf: string): tf is TimeframeKey {
  return tf in TF_DURATION_S;
}

/**
 * Apply live ticker prices to every loaded timeframe for that symbol so the
 * chart candle and header move with the watchlist:
 * - tick in the last candle's slot → update its close/high/low
 * - tick in a later slot           → open a new candle at the previous close
 * - tick older than the last candle → ignore
 * Histories not aligned to the timeframe grid (e.g. CoinGecko OHLC, whose
 * granularity differs from the chart timeframe) are only patched, never
 * extended, so we don't append mismatched candles.
 */
export function patchLiveCandles(
  candles: Record<string, Candle[]>,
  incoming: TokenPrice[],
): Record<string, Candle[]> {
  let out = candles;
  for (const p of incoming) {
    const price = p.priceUsd;
    if (!(price > 0)) continue;
    const tsS = (p.timestamp ?? Date.now()) / 1000;

    for (const key of Object.keys(candles)) {
      const [sym, tf] = key.split(':');
      if (sym !== p.symbol || !tf || !isTimeframe(tf)) continue;
      const arr  = out[key];
      const last = arr?.at(-1);
      if (!arr || !last || tsS < last.time) continue;

      const slot    = slotStart(tsS, tf);
      const aligned = slotStart(last.time, tf) === last.time;
      let next: Candle[];

      if (aligned && slot > last.time) {
        const open = last.close;
        next = [...arr, {
          time: slot, open, high: Math.max(open, price), low: Math.min(open, price), close: price, volume: 0,
        }].slice(-CHART_CANDLE_LIMIT);
      } else {
        if (last.close === price) continue;
        next = [...arr.slice(0, -1), {
          ...last, close: price, high: Math.max(last.high, price), low: Math.min(last.low, price),
        }];
      }

      if (out === candles) out = { ...candles };
      out[key] = next;
    }
  }
  return out;
}
